import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import ParentProtectedRoute from "./ParentProtectedRoute";
import MainLayout from "../layouts/MainLayout";
import ParentLayout from "../layouts/ParentLayout";
// ── Eager (always needed on first paint) ─────────────────────────────────────
import SignIn          from "../pages/authentication/SignIn";
import ForgotPassword  from "../pages/authentication/ForgotPassword";
import ResetPassword   from "../pages/authentication/ResetPassword";
import PageNotFound    from "../pages/PageNotFound/PageNotFound";

// ── Page-level spinner shown while a chunk loads ──────────────────────────────
const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
    <div style={{ width: 36, height: 36, border: "3px solid #e2e8f0", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Lazy pages ────────────────────────────────────────────────────────────────
const SuperAdminDashboard          = lazy(() => import("../pages/Dashboard/SuperAdminDashboard"));
const MyProfile                    = lazy(() => import("../pages/Profile/MyProfile"));

// Master
const Menu                         = lazy(() => import("../pages/Master/Menu"));
const Page                         = lazy(() => import("../pages/Master/Page"));
const Role                         = lazy(() => import("../pages/Master/Role"));

// Notifications
const Notifications                = lazy(() => import("../pages/ManageNotification/Notifications"));

// Templates
const EmailTemplates               = lazy(() => import("../pages/ManageTemplate/EmailTemplates"));

// Reports
const ContactUs                    = lazy(() => import("../pages/Reports/ContactUs"));
const RevenueReport                = lazy(() => import("../pages/ReportAnalytics/RevenueReport"));
const ClientReport                 = lazy(() => import("../pages/ReportAnalytics/ClientReport"));
const FinanceReport                = lazy(() => import("../pages/ReportAnalytics/FinanceReport"));
const HRReport                     = lazy(() => import("../pages/ReportAnalytics/HRReport"));

// Broadcast
const BroadcastEmail               = lazy(() => import("../pages/Broadcast/BroadcastEmail"));

// Communication
const WhatsApp                     = lazy(() => import("../pages/Communication/WhatsApp"));

// Finance
const Payments                     = lazy(() => import("../pages/Finance/Payments"));
const RecurringInvoicesPage        = lazy(() => import("../pages/Finance/RecurringInvoices"));

// Activities
const CalendarPage                 = lazy(() => import("../pages/Activities/CalendarPage"));
const MeetingsPage                 = lazy(() => import("../pages/Activities/Meetings"));

// Invoicing
const Invoices                     = lazy(() => import("../pages/Invoicing/Invoices"));
const CreateInvoicePage            = lazy(() => import("../pages/Invoicing/CreateInvoicePage"));

// Clients & Expenses
const Clients                      = lazy(() => import("../pages/Clients/Clients"));
const Expenses                     = lazy(() => import("../pages/ExpenseTracker/Expenses"));

// Client Management (full-page forms, same as Employee)
const AddClient                    = lazy(() => import("../pages/Client/AddClient"));
const ManageClientsFull            = lazy(() => import("../pages/Client/ManageClients"));

// School Management
const ManageClasses                = lazy(() => import("../pages/AcademicClass/ManageClasses"));
const AddStudent                   = lazy(() => import("../pages/Student/AddStudent"));
const ManageStudents                = lazy(() => import("../pages/Student/ManageStudents"));
const ManageSubjects               = lazy(() => import("../pages/Subject/ManageSubjects"));
const ManageTimetable              = lazy(() => import("../pages/Timetable/ManageTimetable"));
const MyTimetable                  = lazy(() => import("../pages/Timetable/MyTimetable"));
const MarkAttendance               = lazy(() => import("../pages/StudentAttendance/MarkAttendance"));
const ManageFeeStructure           = lazy(() => import("../pages/FeeStructure/ManageFeeStructure"));
const ManageExams                  = lazy(() => import("../pages/Exam/ManageExams"));
const FeeDues                      = lazy(() => import("../pages/FeeStructure/FeeDues"));
const BulkImportStudents           = lazy(() => import("../pages/Student/BulkImportStudents"));
const ManageAcademicYears          = lazy(() => import("../pages/AcademicYear/ManageAcademicYears"));
const SubstituteTeachers           = lazy(() => import("../pages/Timetable/SubstituteTeachers"));
const ManageLibrary                = lazy(() => import("../pages/Library/ManageLibrary"));
const ManageTransport              = lazy(() => import("../pages/Transport/ManageTransport"));

// Parent Portal
const ParentLogin        = lazy(() => import("../pages/parent/ParentLogin"));
const ParentRegister     = lazy(() => import("../pages/parent/ParentRegister"));
const ParentDashboard    = lazy(() => import("../pages/parent/ParentDashboard"));
const ParentAttendance   = lazy(() => import("../pages/parent/ParentAttendance"));
const ParentFees         = lazy(() => import("../pages/parent/ParentFees"));
const ParentTimetable    = lazy(() => import("../pages/parent/ParentTimetable"));
const ParentReportCards  = lazy(() => import("../pages/parent/ParentReportCards"));

// Employee
const AddEmployee                  = lazy(() => import("../pages/Employee/AddEmployee"));
const ManageEmployees              = lazy(() => import("../pages/Employee/ManageEmployees"));

// System Settings
const GeneralSettings  = lazy(() => import("../pages/Settings/GeneralSettings"));
const EmailSettings    = lazy(() => import("../pages/Settings/EmailSettings"));
const InvoiceSettings  = lazy(() => import("../pages/Settings/InvoiceSettings"));
const PaymentSettings  = lazy(() => import("../pages/Settings/PaymentSettings"));
const Integrations     = lazy(() => import("../pages/Settings/Integrations"));

// Administrative Tools
const ManageAdmins                 = lazy(() => import("../pages/AdministrativeTool/ManageAdmins"));
const ManageRolePermission         = lazy(() => import("../pages/AdministrativeTool/ManageRolePermission"));
const ManageEmployeePagePermission = lazy(() => import("../pages/AdministrativeTool/ManageEmployeePagePermission"));
const ManageDatabase               = lazy(() => import("../pages/AdministrativeTool/ManageDatabase"));
const OtpSettings                  = lazy(() => import("../pages/AdministrativeTool/OtpSettings"));
const LoginAttemptLog              = lazy(() => import("../pages/AdministrativeTool/LoginAttemptLog"));
const ActivityLog                  = lazy(() => import("../pages/AdministrativeTool/ActivityLog"));
const SystemHealth                 = lazy(() => import("../pages/AdministrativeTool/SystemHealth"));
const AdminOnlineStatus            = lazy(() => import("../pages/AdministrativeTool/AdminOnlineStatus"));
const AdminSupport                 = lazy(() => import("../pages/Support/AdminSupport"));
const SupportTickets               = lazy(() => import("../pages/Support/SupportTickets"));
const KnowledgeBase                = lazy(() => import("../pages/Support/KnowledgeBase"));

// HR
const Attendance                   = lazy(() => import("../pages/HR/Attendance"));
const LeaveManagement              = lazy(() => import("../pages/HR/LeaveManagement"));
const PayrollPage                  = lazy(() => import("../pages/HR/Payroll"));

// ── Router ────────────────────────────────────────────────────────────────────
const AppRoutes = () => (
  <Routes>
    <Route path="/sign-in/"          element={<SignIn />} />
    <Route path="/forgot-password"   element={<ForgotPassword />} />
    <Route path="/reset-password"    element={<ResetPassword />} />

    <Route
      element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }
    >
      <Route element={<Suspense fallback={<PageLoader />}><SuperAdminDashboard /></Suspense>} path="/" />

      {/* Profile */}
      <Route path="/my-profile/" element={<Suspense fallback={<PageLoader />}><MyProfile /></Suspense>} />

      {/* Master */}
      <Route path="/master/menu" element={<Suspense fallback={<PageLoader />}><Menu /></Suspense>} />
      <Route path="/master/page" element={<Suspense fallback={<PageLoader />}><Page /></Suspense>} />
      <Route path="/master/role" element={<Suspense fallback={<PageLoader />}><Role /></Suspense>} />

      {/* Notifications */}
      <Route path="/manage-notifications" element={<Suspense fallback={<PageLoader />}><Notifications /></Suspense>} />

      {/* Templates */}
      <Route path="/template-management/email-templates" element={<Suspense fallback={<PageLoader />}><EmailTemplates /></Suspense>} />

      {/* Reports */}
      <Route path="/reports/contact-us"   element={<Suspense fallback={<PageLoader />}><ContactUs /></Suspense>} />
      <Route path="/reports/revenue"      element={<Suspense fallback={<PageLoader />}><RevenueReport /></Suspense>} />
      <Route path="/reports/clients"      element={<Suspense fallback={<PageLoader />}><ClientReport /></Suspense>} />
      <Route path="/reports/finance"      element={<Suspense fallback={<PageLoader />}><FinanceReport /></Suspense>} />
      <Route path="/reports/hr"           element={<Suspense fallback={<PageLoader />}><HRReport /></Suspense>} />

      {/* Broadcast */}
      <Route path="/broadcast/email" element={<Suspense fallback={<PageLoader />}><BroadcastEmail /></Suspense>} />

      {/* Communication */}
      <Route path="/communication/whatsapp" element={<Suspense fallback={<PageLoader />}><WhatsApp /></Suspense>} />

      {/* Invoicing */}
      <Route path="/invoices"        element={<Suspense fallback={<PageLoader />}><Invoices /></Suspense>} />
      <Route path="/invoices/create" element={<Suspense fallback={<PageLoader />}><CreateInvoicePage /></Suspense>} />

      {/* Clients & Expenses */}
      <Route path="/clients"  element={<Suspense fallback={<PageLoader />}><Clients /></Suspense>} />
      <Route path="/expenses" element={<Suspense fallback={<PageLoader />}><Expenses /></Suspense>} />

      {/* Client Management (full-page forms) */}
      <Route path="/client/manage"   element={<Suspense fallback={<PageLoader />}><ManageClientsFull /></Suspense>} />
      <Route path="/client/add"      element={<Suspense fallback={<PageLoader />}><AddClient /></Suspense>} />
      <Route path="/client/edit/:id" element={<Suspense fallback={<PageLoader />}><AddClient /></Suspense>} />

      {/* School Management */}
      <Route path="/school/classes"   element={<Suspense fallback={<PageLoader />}><ManageClasses /></Suspense>} />
      <Route path="/student/manage"   element={<Suspense fallback={<PageLoader />}><ManageStudents /></Suspense>} />
      <Route path="/student/add"      element={<Suspense fallback={<PageLoader />}><AddStudent /></Suspense>} />
      <Route path="/student/edit/:id" element={<Suspense fallback={<PageLoader />}><AddStudent /></Suspense>} />
      <Route path="/academics/subjects"   element={<Suspense fallback={<PageLoader />}><ManageSubjects /></Suspense>} />
      <Route path="/academics/timetable"  element={<Suspense fallback={<PageLoader />}><ManageTimetable /></Suspense>} />
      <Route path="/academics/my-timetable" element={<Suspense fallback={<PageLoader />}><MyTimetable /></Suspense>} />
      <Route path="/academics/attendance" element={<Suspense fallback={<PageLoader />}><MarkAttendance /></Suspense>} />
      <Route path="/academics/fee-structure" element={<Suspense fallback={<PageLoader />}><ManageFeeStructure /></Suspense>} />
      <Route path="/academics/exams"      element={<Suspense fallback={<PageLoader />}><ManageExams /></Suspense>} />
      <Route path="/academics/fee-dues"   element={<Suspense fallback={<PageLoader />}><FeeDues /></Suspense>} />
      <Route path="/student/bulk-import"  element={<Suspense fallback={<PageLoader />}><BulkImportStudents /></Suspense>} />
      <Route path="/academics/academic-years" element={<Suspense fallback={<PageLoader />}><ManageAcademicYears /></Suspense>} />
      <Route path="/academics/substitutes"    element={<Suspense fallback={<PageLoader />}><SubstituteTeachers /></Suspense>} />
      <Route path="/academics/library"        element={<Suspense fallback={<PageLoader />}><ManageLibrary /></Suspense>} />
      <Route path="/academics/transport"      element={<Suspense fallback={<PageLoader />}><ManageTransport /></Suspense>} />

      {/* Finance */}
      <Route path="/finance/payments"      element={<Suspense fallback={<PageLoader />}><Payments /></Suspense>} />
      <Route path="/invoices/recurring"    element={<Suspense fallback={<PageLoader />}><RecurringInvoicesPage /></Suspense>} />

      {/* Activities */}
      <Route path="/calendar"              element={<Suspense fallback={<PageLoader />}><CalendarPage /></Suspense>} />
      <Route path="/activities/meetings"   element={<Suspense fallback={<PageLoader />}><MeetingsPage /></Suspense>} />

      {/* Employee */}
      <Route path="/employee/add"      element={<Suspense fallback={<PageLoader />}><AddEmployee /></Suspense>} />
      <Route path="/employee/edit/:id" element={<Suspense fallback={<PageLoader />}><AddEmployee /></Suspense>} />
      <Route path="/employee/manage"   element={<Suspense fallback={<PageLoader />}><ManageEmployees /></Suspense>} />

      {/* Administrative Tools */}
      <Route path="/admininstrative-tools/admins"                    element={<Suspense fallback={<PageLoader />}><ManageAdmins /></Suspense>} />
      <Route path="/admininstrative-tools/role-permissions"          element={<Suspense fallback={<PageLoader />}><ManageRolePermission /></Suspense>} />
      <Route path="/admininstrative-tools/employee-page-permissions" element={<Suspense fallback={<PageLoader />}><ManageEmployeePagePermission /></Suspense>} />
      <Route path="/admininstrative-tools/db-management"            element={<Suspense fallback={<PageLoader />}><ManageDatabase /></Suspense>} />
      <Route path="/admininstrative-tools/otp-settings"             element={<Suspense fallback={<PageLoader />}><OtpSettings /></Suspense>} />
      <Route path="/admininstrative-tools/login-attempt-log"        element={<Suspense fallback={<PageLoader />}><LoginAttemptLog /></Suspense>} />
      <Route path="/admininstrative-tools/activity-log"             element={<Suspense fallback={<PageLoader />}><ActivityLog /></Suspense>} />
      <Route path="/admininstrative-tools/system-health"            element={<Suspense fallback={<PageLoader />}><SystemHealth /></Suspense>} />
      <Route path="/admininstrative-tools/admin-online-status"      element={<Suspense fallback={<PageLoader />}><AdminOnlineStatus /></Suspense>} />

      {/* System Settings */}
      <Route path="/settings/general"      element={<Suspense fallback={<PageLoader />}><GeneralSettings /></Suspense>} />
      <Route path="/settings/email"        element={<Suspense fallback={<PageLoader />}><EmailSettings /></Suspense>} />
      <Route path="/settings/invoice"      element={<Suspense fallback={<PageLoader />}><InvoiceSettings /></Suspense>} />
      <Route path="/settings/payments"     element={<Suspense fallback={<PageLoader />}><PaymentSettings /></Suspense>} />
      <Route path="/settings/integrations" element={<Suspense fallback={<PageLoader />}><Integrations /></Suspense>} />

      {/* Support */}
      <Route path="/support"          element={<Suspense fallback={<PageLoader />}><AdminSupport /></Suspense>} />
      <Route path="/support/tickets"  element={<Suspense fallback={<PageLoader />}><SupportTickets /></Suspense>} />
      <Route path="/support/kb"       element={<Suspense fallback={<PageLoader />}><KnowledgeBase /></Suspense>} />

      {/* HR */}
      <Route path="/hr/attendance" element={<Suspense fallback={<PageLoader />}><Attendance /></Suspense>} />
      <Route path="/hr/leaves"     element={<Suspense fallback={<PageLoader />}><LeaveManagement /></Suspense>} />
      <Route path="/hr/payroll"    element={<Suspense fallback={<PageLoader />}><PayrollPage /></Suspense>} />
    </Route>

    {/* ── Parent Portal — separate auth surface from the admin panel ──────── */}
    <Route path="/parent/login"    element={<Suspense fallback={<PageLoader />}><ParentLogin /></Suspense>} />
    <Route path="/parent/register" element={<Suspense fallback={<PageLoader />}><ParentRegister /></Suspense>} />
    <Route
      element={
        <ParentProtectedRoute>
          <ParentLayout />
        </ParentProtectedRoute>
      }
    >
      <Route path="/parent/dashboard"     element={<Suspense fallback={<PageLoader />}><ParentDashboard /></Suspense>} />
      <Route path="/parent/attendance"    element={<Suspense fallback={<PageLoader />}><ParentAttendance /></Suspense>} />
      <Route path="/parent/fees"          element={<Suspense fallback={<PageLoader />}><ParentFees /></Suspense>} />
      <Route path="/parent/timetable"     element={<Suspense fallback={<PageLoader />}><ParentTimetable /></Suspense>} />
      <Route path="/parent/report-cards"  element={<Suspense fallback={<PageLoader />}><ParentReportCards /></Suspense>} />
    </Route>

    <Route path="*" element={<PageNotFound />} />
  </Routes>
);

export default AppRoutes;
